import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, tokenStore, unwrap } from "@/api/client";
import { toApiError } from "@/api/errors";
import type { BillingStatus, ManualPayment } from "@/api/types";
import { env } from "@/lib/env";
import { sessionId } from "@/lib/session";

export const billingKeys = {
  all: ["billing"] as const,
  me: ["billing", "me"] as const,
  config: ["billing", "config"] as const,
  account: ["billing", "account"] as const,
  plans: ["billing", "plans"] as const,
  payments: ["billing", "payments"] as const,
  payment: (id: string) => ["billing", "payment", id] as const,
};

export function usePaymentConfig() {
  return useQuery({
    queryKey: billingKeys.config,
    queryFn: () => unwrap(api.GET("/api/v1/payments/config/")),
    staleTime: 5 * 60_000,
  });
}

export function useBillingStatus(enabled = true) {
  return useQuery({
    queryKey: billingKeys.me,
    queryFn: () => unwrap(api.GET("/api/v1/subscriptions/me/")),
    enabled,
    staleTime: 30_000,
  });
}

export function usePayoutAccount() {
  return useQuery({
    queryKey: billingKeys.account,
    queryFn: () => unwrap(api.GET("/api/v1/payments/payout-account/")),
    staleTime: 60_000,
  });
}

export function useBillingPlans() {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: () => unwrap(api.GET("/api/v1/subscriptions/plans/")),
    staleTime: 60_000,
  });
}

export function usePaymentHistory() {
  return useQuery({
    queryKey: billingKeys.payments,
    queryFn: () => unwrap(api.GET("/api/v1/payments/manual/", { params: { query: { page: 1 } } })),
  });
}

/** One payment. While it's being reviewed the screen re-checks every 30 seconds. */
export function useManualPayment(id: string | undefined) {
  return useQuery({
    queryKey: billingKeys.payment(id ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/payments/manual/{id}/", { params: { path: { id: id! } } })),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ["SUBMITTED", "UNDER_REVIEW"].includes(q.state.data.status) ? 30_000 : false),
    refetchIntervalInBackground: false,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { plan_id: string; interval: "monthly" | "yearly" }) => unwrap(api.POST("/api/v1/payments/manual/", { body })),
    onSuccess: (payment) => {
      qc.setQueryData(billingKeys.payment(payment.id), payment);
      void qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/** Wallet top-up by bank transfer: creates the invoice (amount, reference, our account). */
export function useCreateTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (amount: number) => unwrap(api.POST("/api/v1/payments/manual/topup/", { body: { amount } })),
    onSuccess: (payment) => {
      qc.setQueryData(billingKeys.payment(payment.id), payment);
    },
  });
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/v1/payments/manual/{id}/cancel/", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: billingKeys.all }),
  });
}

export interface ProofForm {
  file: File;
  senderName: string;
  senderBank: string;
  amountNaira: string;
  transferAt: string; // ISO
  note: string;
}

/**
 * Multipart proof upload with real progress (XMLHttpRequest: fetch can't report upload progress).
 * Refreshes the session once on 401, like the API client does.
 */
export function uploadProof(
  id: string,
  form: ProofForm,
  { resubmit = false, onProgress }: { resubmit?: boolean; onProgress?: (fraction: number) => void } = {},
): Promise<ManualPayment> {
  const send = (token: string | null) =>
    new Promise<{ status: number; body: unknown }>((resolve) => {
      const data = new FormData();
      data.append("proof_file", form.file, form.file.name);
      data.append("sender_name", form.senderName.trim());
      data.append("sender_bank", form.senderBank.trim());
      data.append("amount_sent", form.amountNaira);
      data.append("transfer_datetime", form.transferAt);
      data.append("vendor_note", form.note.trim());
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${env.apiBaseUrl}/payments/manual/${id}/${resubmit ? "resubmit" : "proof"}/`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-Client", "web");
      const sid = sessionId();
      if (sid) xhr.setRequestHeader("X-Session-ID", sid);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          body = null;
        }
        resolve({ status: xhr.status, body });
      };
      xhr.onerror = () => resolve({ status: 0, body: null });
      xhr.send(data);
    });
  return (async () => {
    let res = await send(tokenStore.get());
    if (res.status === 401) {
      const { refreshAccessToken } = await import("@/api/client");
      const token = await refreshAccessToken();
      if (token) res = await send(token);
    }
    if (res.status >= 200 && res.status < 300) return res.body as ManualPayment;
    throw toApiError(res.status, res.body);
  })();
}

export function useSubmitProof(id: string, resubmit = false) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ form, onProgress }: { form: ProofForm; onProgress?: (f: number) => void }) =>
      uploadProof(id, form, { resubmit, onProgress }),
    onSuccess: (payment) => {
      qc.setQueryData(billingKeys.payment(id), payment);
      void qc.invalidateQueries({ queryKey: billingKeys.all });
      void qc.invalidateQueries({ queryKey: ["vendor", "subscription"] });
    },
  });
}

export async function downloadReceipt(payment: ManualPayment): Promise<void> {
  const res = await fetch(`${env.apiBaseUrl}/payments/manual/${payment.id}/receipt/`, {
    headers: { Authorization: `Bearer ${tokenStore.get() ?? ""}`, "X-Client": "web" },
    credentials: "include",
  });
  if (!res.ok) throw toApiError(res.status, await res.json().catch(() => null));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `RealityStore-receipt-${payment.reference}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export type { BillingStatus };
